/**
 * multi-goal — data layer (feature FS-173).
 *
 * Typed wrapper around `apiClient` for:
 *   GET    /api/v1/multi-goal?campaign_id=
 *   POST   /api/v1/multi-goal
 *   PATCH  /api/v1/multi-goal/:id
 *   DELETE /api/v1/multi-goal/:id
 *   POST   /api/v1/multi-goal/:id/fire
 *   GET    /api/v1/multi-goal/:id/conversions
 *
 * Pure helpers (validators, formatters, query keys) are exported so the
 * page component and the unit tests can share contracts with the server.
 */
import { apiClient } from "../../api-client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CampaignGoal {
	id: string;
	campaign_id: string;
	name: string;
	slug: string;
	payout_cents: number;
	cap_per_click: number;
	active: boolean;
	created_at: number;
	updated_at: number;
	deleted_at: number | null;
}

export interface GoalConversion {
	id: string;
	goal_id: string;
	click_id: string;
	payout_cents: number;
	created_at: number;
}

export interface ListGoalsResponse {
	goals: CampaignGoal[];
}

export interface ListConversionsResponse {
	conversions: GoalConversion[];
}

// ---------------------------------------------------------------------------
// Validators — mirror the server so the UI can short-circuit bad input.
// ---------------------------------------------------------------------------

const SLUG_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

export const NAME_MAX = 120;
export const PAYOUT_CENTS_MAX = 100_000_000_000;
export const CAP_MAX = 1_000_000;

export function validateName(raw: string): string | null {
	const trimmed = String(raw ?? "").trim();
	if (trimmed.length < 1 || trimmed.length > NAME_MAX) return null;
	return trimmed;
}

export function validateSlug(raw: string): string | null {
	const normalized = String(raw ?? "")
		.trim()
		.toLowerCase();
	if (!SLUG_RE.test(normalized)) return null;
	return normalized;
}

export function validateIdLike(raw: string): string | null {
	if (typeof raw !== "string") return null;
	if (!ID_RE.test(raw)) return null;
	return raw;
}

export function validatePayoutCents(
	raw: string | number | null | undefined,
): number | "invalid" {
	if (raw === null || raw === undefined || raw === "") return 0;
	const n = typeof raw === "number" ? raw : Number(String(raw).trim());
	if (
		!Number.isFinite(n) ||
		!Number.isInteger(n) ||
		n < 0 ||
		n > PAYOUT_CENTS_MAX
	) {
		return "invalid";
	}
	return n;
}

export function validateCapPerClick(
	raw: string | number | null | undefined,
): number | "invalid" {
	if (raw === null || raw === undefined || raw === "") return 1;
	const n = typeof raw === "number" ? raw : Number(String(raw).trim());
	if (
		!Number.isFinite(n) ||
		!Number.isInteger(n) ||
		n < 1 ||
		n > CAP_MAX
	) {
		return "invalid";
	}
	return n;
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const multiGoalQueryKeys = {
	all: ["multi-goal"] as const,
	list: (campaignId: string) =>
		[...multiGoalQueryKeys.all, "list", campaignId] as const,
	conversions: (goalId: string) =>
		[...multiGoalQueryKeys.all, "conversions", goalId] as const,
};

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

export function formatCents(cents: number): string {
	if (!Number.isFinite(cents)) return "--";
	const dollars = cents / 100;
	try {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		}).format(dollars);
	} catch {
		return `$${dollars.toFixed(2)}`;
	}
}

export function formatCount(n: number): string {
	if (!Number.isFinite(n) || n < 0) return "0";
	return n.toLocaleString("en-US");
}

export function formatTimestampMs(ms: number): string {
	if (!Number.isFinite(ms) || ms <= 0) return "--";
	return new Date(ms).toLocaleString("en-US", {
		year: "numeric",
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	});
}

export function capLabel(cap: number): string {
	if (cap === 1) return "1 per click";
	return `${formatCount(cap)} per click`;
}

// ---------------------------------------------------------------------------
// Remote bindings
// ---------------------------------------------------------------------------

const BASE = "/multi-goal";

export async function listGoals(campaignId: string): Promise<CampaignGoal[]> {
	const res = await apiClient.get<ListGoalsResponse>(BASE, {
		query: { campaign_id: campaignId },
	});
	return res.goals;
}

export interface CreateGoalArgs {
	campaign_id: string;
	name: string;
	slug: string;
	payout_cents: number;
	cap_per_click: number;
	active: boolean;
}

export async function createGoal(args: CreateGoalArgs): Promise<CampaignGoal> {
	const res = await apiClient.post<{ goal: CampaignGoal }>(BASE, {
		campaign_id: args.campaign_id,
		name: args.name,
		slug: args.slug,
		payout_cents: args.payout_cents,
		cap_per_click: args.cap_per_click,
		active: args.active,
	});
	return res.goal;
}

export interface UpdateGoalArgs {
	id: string;
	name?: string;
	slug?: string;
	payout_cents?: number;
	cap_per_click?: number;
	active?: boolean;
}

export async function updateGoal(args: UpdateGoalArgs): Promise<CampaignGoal> {
	const payload: Record<string, unknown> = {};
	if (args.name !== undefined) payload.name = args.name;
	if (args.slug !== undefined) payload.slug = args.slug;
	if (args.payout_cents !== undefined) payload.payout_cents = args.payout_cents;
	if (args.cap_per_click !== undefined)
		payload.cap_per_click = args.cap_per_click;
	if (args.active !== undefined) payload.active = args.active;
	const res = await apiClient.patch<{ goal: CampaignGoal }>(
		`${BASE}/${encodeURIComponent(args.id)}`,
		payload,
	);
	return res.goal;
}

export async function deleteGoal(id: string): Promise<void> {
	await apiClient.delete(`${BASE}/${encodeURIComponent(id)}`);
}

export async function fireGoal(args: {
	id: string;
	click_id: string;
}): Promise<GoalConversion> {
	const res = await apiClient.post<{ conversion: GoalConversion }>(
		`${BASE}/${encodeURIComponent(args.id)}/fire`,
		{ click_id: args.click_id },
	);
	return res.conversion;
}

export async function listConversions(
	goalId: string,
	limit = 100,
): Promise<GoalConversion[]> {
	const res = await apiClient.get<ListConversionsResponse>(
		`${BASE}/${encodeURIComponent(goalId)}/conversions`,
		{ query: { limit } },
	);
	return res.conversions;
}
